import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Avatar,
  Chip,
  Grid,
  Divider,
} from '@mui/material';
import {
  AccountCircle,
  Logout as LogoutIcon,
  Stars,
  AttachMoney,
  Lock as LockIcon,
  QrCode2 as QrCodeIcon,
  CalendarToday as CalendarIcon,
  Wc as GenderIcon,
  Phone as PhoneIcon,
  MarkEmailRead as EmailVerifyIcon,
} from '@mui/icons-material';
import { Alert, AlertTitle, CircularProgress } from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import { useCurrency } from '../contexts/CurrencyContext';
import { useNavigate } from 'react-router-dom';
import WeeklyLifeCard from '../components/WeeklyLifeCard';
import ChartSummaryCard from '../components/dashboard/ChartSummaryCard';
import PalaceExploreCard from '../components/dashboard/PalaceExploreCard';
import OnboardingWizard from '../components/OnboardingWizard';
import AnnouncementBanner from '../components/common/AnnouncementBanner';
import UserChangePasswordDialog from '../components/UserChangePasswordDialog';
import ShareSettingsDialog from '../components/ShareSettingsDialog';
import DailyLifeCard from '../components/DailyLifeCard';
import LearningProgressCard from '../components/LearningProgressCard';
import { getMonthlyFreeStatus, } from '../api/qa';
import { getDailyFortune, DailyFortuneResponse } from '../api/dailyfortune';
import { WeeklyAdviceResponse } from '../api/weeklylife';
import { activateEmailBonus } from '../api/auth';
import { reloadFirebaseUserAndGetToken, resendVerificationEmail } from '../services/firebaseAuth';

/**
 * 將時辰代碼轉換為時辰名稱
 */
const getHourName = (hour: string): string => {
  const hourMap: { [key: string]: string } = {
    '23': '子時', '01': '丑時', '03': '寅時', '05': '卯時',
    '07': '辰時', '09': '巳時', '11': '午時', '13': '未時',
    '15': '申時', '17': '酉時', '19': '戌時', '21': '亥時',
  };
  return hourMap[hour] || hour;
};

/**
 * 格式化出生時間 (YYYYMMDDhh -> YYYY年 M月 D日 X時)
 */
const formatBirthTime = (birthTime: string | undefined): string => {
  if (!birthTime || birthTime.length !== 10) return birthTime || '未設定';
  let y = parseInt(birthTime.substring(0, 4), 10);
  let m = parseInt(birthTime.substring(4, 6), 10);
  let d = parseInt(birthTime.substring(6, 8), 10);
  const hour = birthTime.substring(8, 10);
  // 子時（hh=23）命盤計算日 = 儲存日 +1 天
  if (hour === '23') {
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() + 1);
    y = dt.getFullYear(); m = dt.getMonth() + 1; d = dt.getDate();
  }
  return `${y}年 ${m}月 ${d}日 ${getHourName(hour)}`;
};

/**
 * 主頁面 / 儀表板
 */
export const DashboardPage: React.FC = () => {
  const { user, logout, getCurrentUser } = useAuth();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [monthlyFreeAvailable, setMonthlyFreeAvailable] = useState<boolean>(false);
  const [emailBannerLoading, setEmailBannerLoading] = useState(false);
  const [emailBannerMsg, setEmailBannerMsg] = useState<string | null>(null);

  // 從子元件 hoist 的資料，用於跨卡片共享
  const [fortune, setFortune] = useState<DailyFortuneResponse | null>(null);
  const [weeklyAdvice, setWeeklyAdvice] = useState<WeeklyAdviceResponse | null>(null);

  // 載入幸運元素（原本由 DailyFortuneCard 負責，現併入 DailyLifeCard 顯示）
  useEffect(() => {
    getDailyFortune()
      .then(data => setFortune(data))
      .catch(err => console.error('載入每日幸運元素失敗:', err));
  }, []);

  const handleWeeklyAdviceLoad = useCallback((data: WeeklyAdviceResponse) => {
    setWeeklyAdvice(data);
  }, []);

  useEffect(() => {
    getCurrentUser().catch(() => {});
  }, [getCurrentUser]);

  useEffect(() => {
    if (user) {
      getMonthlyFreeStatus().then(res => {
        if (res.success) setMonthlyFreeAvailable(res.monthly_free_available);
      });
    }
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const handleActivateBonus = async () => {
    setEmailBannerLoading(true);
    setEmailBannerMsg(null);
    try {
      const { success, emailVerified, idToken, error } = await reloadFirebaseUserAndGetToken();
      if (!success || !idToken) {
        setEmailBannerMsg(error || '無法取得 Firebase 狀態，請重新整理頁面');
        return;
      }
      if (!emailVerified) {
        setEmailBannerMsg('Email 尚未驗證，請先點擊驗證信中的連結再試');
        return;
      }
      const result = await activateEmailBonus(idToken);
      if (result.success) {
        setEmailBannerMsg(`✓ ${result.message}`);
        await getCurrentUser();
      } else {
        setEmailBannerMsg(result.message);
      }
    } catch (err: any) {
      setEmailBannerMsg(err.message || '發生錯誤，請稍後再試');
    } finally {
      setEmailBannerLoading(false);
    }
  };

  const handleResendEmail = async () => {
    setEmailBannerLoading(true);
    setEmailBannerMsg(null);
    try {
      const { success, error } = await resendVerificationEmail();
      setEmailBannerMsg(success ? '驗證信已重新寄出，請查收信箱' : (error || '寄送失敗'));
    } finally {
      setEmailBannerLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        {/* 系統公告 */}
        <AnnouncementBanner maxDisplay={3} />

        {/* Email 驗證提示（未驗證且點數為 0 的新用戶才顯示）*/}
        {user && user.is_email_verified === 0 && (
          <Alert
            severity="warning"
            icon={<EmailVerifyIcon />}
            sx={{ mb: 2 }}
            action={
              emailBannerLoading ? (
                <CircularProgress size={20} sx={{ mr: 1, mt: 0.5 }} />
              ) : (
                <Box display="flex" gap={1}>
                  <Button size="small" variant="outlined" color="warning" onClick={handleResendEmail}>
                    重寄驗證信
                  </Button>
                  <Button size="small" variant="contained" color="warning" onClick={handleActivateBonus}>
                    已驗證，領取點數
                  </Button>
                </Box>
              )
            }
          >
            <AlertTitle>請驗證您的 Email</AlertTitle>
            驗證後即可獲得 499 點體驗點數（30 天內有效）。
            <br />
            驗證信已寄到 <strong>{user.email}</strong>，若未收到請也檢查「垃圾郵件」或「Junk」資料夾。
            <br />
            收到信後請點擊信中的「<strong>Complete Verification（完成驗證）</strong>」按鈕，直到出現「已驗證」訊息才算完成，再回到此頁點擊「已驗證，領取點數」。
            {emailBannerMsg && (
              <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
                {emailBannerMsg}
              </Typography>
            )}
          </Alert>
        )}

        {/* ── 歡迎回來（含帳戶資訊 + 命盤資訊）── */}
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          {/* 頂部：問候 + 操作按鈕 */}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                <AccountCircle fontSize="large" />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  歡迎回來，{user?.full_name}！
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user?.email}
                </Typography>
              </Box>
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button variant="outlined" startIcon={<QrCodeIcon />} onClick={() => setShareDialogOpen(true)}>
                分享命盤
              </Button>
              <Button variant="outlined" startIcon={<LockIcon />} onClick={() => setChangePasswordOpen(true)}>
                修改密碼
              </Button>
              <Button variant="outlined" color="error" startIcon={<LogoutIcon />} onClick={handleLogout}>
                登出
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* 帳戶資訊 + 命盤資訊 並排 */}
          <Grid container spacing={3}>
            {/* 帳戶資訊 */}
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <AttachMoney color="success" />
                <Typography variant="subtitle1" fontWeight="bold">帳戶資訊</Typography>
              </Box>

              {/* 體驗點數 */}
              {(user?.bonus_points !== undefined && user.bonus_points > 0) && (
                <Box mb={1}>
                  <Typography variant="body2" color="text.secondary">體驗點數</Typography>
                  <Box display="flex" alignItems="baseline" gap={1}>
                    <Typography variant="h6" fontWeight="bold" color="primary.main">
                      {user.bonus_points} 點
                    </Typography>
                    {user?.bonus_points_expires_at && (
                      <Typography variant="caption" color="text.secondary">
                        （到期：{new Date(user.bonus_points_expires_at).toLocaleDateString('zh-TW')}）
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {/* 帳戶餘額 */}
              <Box mb={1}>
                <Typography variant="body2" color="text.secondary">帳戶餘額</Typography>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color={(user?.balance ?? 0) < 0 ? 'error.main' : 'success.main'}
                >
                  {formatPrice(user?.balance || 0)}
                </Typography>
              </Box>

              {/* 每月免費問題 */}
              <Box mb={1}>
                <Typography variant="body2" color="text.secondary">每月免費問題</Typography>
                <Chip
                  label={monthlyFreeAvailable ? '剩 1 次（MAIN / COMMON 類）' : '本月已使用'}
                  color={monthlyFreeAvailable ? 'success' : 'default'}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Box>

              {/* 負餘額警告 */}
              {(user?.balance ?? 0) < 0 && (
                <Box sx={{ p: 1.5, bgcolor: 'warning.light', borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}>
                  <Typography variant="body2" color="warning.dark" fontWeight="bold">
                    帳戶餘額為負值
                  </Typography>
                  <Typography variant="body2" color="warning.dark">
                    超過 -500 元將停止服務
                  </Typography>
                </Box>
              )}

              <Chip
                label={user?.is_active ? '帳號正常' : (user?.balance ?? 0) <= -500 ? '需加值' : '帳號停用'}
                color={user?.is_active && (user?.balance ?? 0) > -500 ? 'success' : 'error'}
                size="small"
                sx={{ mt: 1 }}
              />
            </Grid>

            {/* 命盤資訊 */}
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <Stars color="primary" />
                <Typography variant="subtitle1" fontWeight="bold">命盤資訊</Typography>
              </Box>

              <Box display="flex" flexDirection="column" gap={0.75}>
                <Box display="flex" alignItems="center" gap={1}>
                  <CalendarIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">出生時間</Typography>
                  <Typography variant="body2" fontWeight={500}>{formatBirthTime(user?.birth_time)}</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <GenderIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">性別</Typography>
                  <Typography variant="body2" fontWeight={500}>{user?.gender === 'Male' ? '男命' : '女命'}</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <PhoneIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">電話</Typography>
                  <Typography variant="body2" fontWeight={500}>{user?.phone_number}</Typography>
                </Box>
              </Box>

              <Button variant="outlined" size="small" sx={{ mt: 2 }} onClick={() => navigate('/chart')}>
                查看完整命盤
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* ── 主要卡片區 ── */}
        <Grid container spacing={3}>

          {/* 1. 今日全人生活建議（含幸運元素） */}
          <Grid item xs={12}>
            <DailyLifeCard
              autoLoad={true}
              weeklyAdvice={weeklyAdvice}
              fortune={fortune}
            />
          </Grid>

          {/* 2. 本週生活建議（全寬） */}
          <Grid item xs={12}>
            <WeeklyLifeCard autoLoad={true} onLoad={handleWeeklyAdviceLoad} />
          </Grid>

          {/* 3. 命宮主星特色（含本月命盤提示） + 命盤探索地圖（同行） */}
          <Grid item xs={12} md={5}>
            <ChartSummaryCard
              chartTip={fortune?.chart_tip}
              recommendedQuestionId={fortune?.recommended_question_id}
              recommendedQuestionText={fortune?.recommended_question_text}
            />
          </Grid>

          <Grid item xs={12} md={7}>
            <PalaceExploreCard />
          </Grid>

          {/* 4. 我的學習旅程（全寬） */}
          <Grid item xs={12}>
            <LearningProgressCard autoLoad={true} />
          </Grid>

          {/* 5. 快速功能 */}
          <Grid item xs={12}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                快速功能
              </Typography>
              <Grid container spacing={2} mt={1}>
                <Grid item xs={12} sm={6} md={3}>
                  <Button variant="contained" fullWidth size="large" onClick={() => navigate('/chart')}>
                    查看命盤
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button variant="contained" fullWidth size="large" onClick={() => navigate('/questions')}>
                    問題查詢
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button variant="contained" fullWidth size="large" onClick={() => navigate('/history')}>
                    查詢歷史
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button variant="contained" fullWidth size="large" onClick={() => navigate('/pair-analysis')}>
                    配對分析
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <UserChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
      <ShareSettingsDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
      />

      {user?.created_at && (
        <OnboardingWizard userCreatedAt={user.created_at} />
      )}
    </Box>
  );
};

export default DashboardPage;
